// src/components/HelpAssistant.jsx
import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Fab,
  Paper,
  Stack,
  TextField,
  IconButton,
  Typography,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import { useLocation } from "react-router-dom";
import axios from "../config/axiosConfig";
import { getSetting } from "../services/settingsService";

export default function HelpAssistant() {
  const location = useLocation();
  const [enabled, setEnabled] = useState(false);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const listRef = useRef(null);

  useEffect(() => {
    let alive = true;
    getSetting("help_assistant_enabled")
      .then(({ data }) => {
        if (alive) setEnabled(data?.valor === "1");
      })
      .catch(() => {
        if (alive) setEnabled(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
    });
  };

  const handleSend = async () => {
    const mensaje = input.trim();
    if (!mensaje || loading) return;

    setMessages((prev) => [...prev, { role: "user", text: mensaje }]);
    setInput("");
    setLoading(true);
    setError("");
    scrollToBottom();

    try {
      const { data } = await axios.post("/api/ayuda-modulo", {
        pathname: location.pathname,
        mensaje,
      });
      setMessages((prev) => [...prev, { role: "assistant", text: data?.text || "" }]);
    } catch (e) {
      setError(
        e?.response?.data?.error || e?.message || "No se pudo obtener respuesta."
      );
    } finally {
      setLoading(false);
      scrollToBottom();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!enabled) return null;

  return (
    <>
      <Tooltip title="Ayuda de este módulo">
        <Fab
          color="primary"
          size="medium"
          onClick={() => setOpen((v) => !v)}
          sx={{ position: "fixed", right: 20, bottom: 20, zIndex: 1300 }}
        >
          {open ? <CloseIcon /> : <HelpOutlineIcon />}
        </Fab>
      </Tooltip>

      {open && (
        <Paper
          elevation={6}
          sx={{
            position: "fixed",
            right: 20,
            bottom: 88,
            width: 340,
            maxWidth: "calc(100vw - 40px)",
            height: 420,
            display: "flex",
            flexDirection: "column",
            borderRadius: 2,
            overflow: "hidden",
            zIndex: 1300,
          }}
        >
          <Box sx={{ px: 1.5, py: 1, bgcolor: "primary.main", color: "#fff" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Ayuda de este módulo
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              Respuestas generadas por IA, puede contener errores.
            </Typography>
          </Box>

          <Box ref={listRef} sx={{ flexGrow: 1, overflowY: "auto", p: 1.25 }}>
            {messages.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                Preguntame algo sobre esta pantalla, por ejemplo "¿para qué sirve esto?".
              </Typography>
            )}
            <Stack spacing={1}>
              {messages.map((m, idx) => (
                <Box
                  key={idx}
                  sx={{
                    alignSelf: m.role === "user" ? "flex-end" : "flex-start",
                    bgcolor: m.role === "user" ? "primary.main" : "action.hover",
                    color: m.role === "user" ? "#fff" : "text.primary",
                    px: 1.25,
                    py: 0.75,
                    borderRadius: 1.5,
                    maxWidth: "85%",
                  }}
                >
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {m.text}
                  </Typography>
                </Box>
              ))}
              {loading && (
                <Box sx={{ alignSelf: "flex-start", px: 1 }}>
                  <CircularProgress size={16} />
                </Box>
              )}
              {error && (
                <Typography variant="caption" color="error">
                  {error}
                </Typography>
              )}
            </Stack>
          </Box>

          <Box sx={{ p: 1, borderTop: "1px solid", borderColor: "divider", display: "flex", gap: 0.5 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Escribe tu pregunta..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
            />
            <IconButton color="primary" onClick={handleSend} disabled={loading || !input.trim()}>
              <SendIcon fontSize="small" />
            </IconButton>
          </Box>
        </Paper>
      )}
    </>
  );
}
